import { createStore } from '@xstate/store';

interface CanvasSelectionContext {
  selectedEntityIds: string[];
  enteredGroupId: string | null;
  isBoxSelecting: boolean;
  editingElementId: string | null;
  editingElementKind: 'textblock' | 'textlabel' | 'blocknode' | null;
}

export const canvasSelectionStore = createStore({
  context: {
    selectedEntityIds: [],
    enteredGroupId: null,
    isBoxSelecting: false,
    editingElementId: null,
    editingElementKind: null,
  } as CanvasSelectionContext,
  on: {
    // === Unified entity selection events ===
    'entity.selectOne': (ctx, event: { entityId: string }) => ({
      ...ctx,
      selectedEntityIds: [event.entityId],
    }),
    'entity.toggle': (ctx, event: { entityId: string }) => {
      const has = ctx.selectedEntityIds.includes(event.entityId);
      return {
        ...ctx,
        selectedEntityIds: has
          ? ctx.selectedEntityIds.filter(id => id !== event.entityId)
          : [...ctx.selectedEntityIds, event.entityId],
      };
    },
    'entity.set': (ctx, event: { entityIds: string[] }) => {
      const { entityIds } = event;
      if (entityIds.length === ctx.selectedEntityIds.length &&
          entityIds.every((id, i) => ctx.selectedEntityIds[i] === id)) {
        return ctx;
      }
      return { ...ctx, selectedEntityIds: entityIds };
    },
    'entity.add': (ctx, event: { entityIds: string[] }) => {
      const existing = new Set(ctx.selectedEntityIds);
      for (const id of event.entityIds) existing.add(id);
      return { ...ctx, selectedEntityIds: [...existing] };
    },
    'entity.prune': (ctx, event: { validIds: Set<string> }) => {
      const pruned = ctx.selectedEntityIds.filter(id => event.validIds.has(id));
      if (pruned.length === ctx.selectedEntityIds.length) return ctx;
      return { ...ctx, selectedEntityIds: pruned };
    },
    'entity.clear': (ctx) => {
      if (ctx.selectedEntityIds.length === 0 && !ctx.isBoxSelecting) return ctx;
      return { ...ctx, selectedEntityIds: [], isBoxSelecting: false };
    },

    // === Group navigation (not selection — separate state) ===
    'group.enter': (ctx, event: { groupId: string }) => ({
      ...ctx,
      enteredGroupId: event.groupId,
      selectedEntityIds: [],
    }),
    'group.exit': (ctx) => {
      if (ctx.enteredGroupId === null) return ctx;
      return { ...ctx, enteredGroupId: null };
    },

    'boxSelect.start': (ctx) => {
      if (ctx.isBoxSelecting) return ctx;
      return { ...ctx, isBoxSelecting: true };
    },
    'boxSelect.end': (ctx) => {
      if (!ctx.isBoxSelecting) return ctx;
      return { ...ctx, isBoxSelecting: false };
    },

    'editing.start': (ctx, event: { elementId: string; kind: 'textblock' | 'textlabel' | 'blocknode' }) => ({
      ...ctx,
      editingElementId: event.elementId,
      editingElementKind: event.kind,
    }),
    'editing.stop': (ctx) => {
      if (!ctx.editingElementId) return ctx;
      return { ...ctx, editingElementId: null, editingElementKind: null };
    },
  },
});
